import { flow, pipe } from 'fp-ts/function';
import { array, option } from 'fp-ts';
import { AccountResult, AccountType, Bill, InlinedAddress, useRepositoryQuery } from '@mews-commander/data-access';
import { EMPTY_ADDRESS_FORM_VALUES } from '../constants';
import { BillOwner } from '../types';
import { inlinedAddressToFormValues } from '../utils';

const getBillOwner =
    ({ customers, companies }: AccountResult) =>
    (bill: Bill) =>
        pipe(
            [
                ...(customers?.map((data) => ({ type: AccountType.Customer as const, data })) ?? []),
                ...(companies?.map((data) => ({ type: AccountType.Company as const, data })) ?? []),
            ],
            array.findFirst(({ data }) => data.id === bill.ownerId)
        );

const getBillOwnerName = (owner: BillOwner) => {
    switch (owner.type) {
        case AccountType.Customer: {
            const { firstName, secondLastName, lastName } = owner.data;
            return pipe([firstName, secondLastName, lastName], array.filterMap(option.fromNullable), (values) =>
                values.join(' ')
            );
        }
        case AccountType.Company: {
            return owner.data.name;
        }
    }
};

const getAddress = flow(
    option.fromNullable<InlinedAddress | null | undefined>,
    option.map(inlinedAddressToFormValues),
    option.getOrElse(() => EMPTY_ADDRESS_FORM_VALUES)
);

export const useFormPrefill = (billId: string, allowCustomerBillAddressObjection: boolean) => {
    const billQuery = useRepositoryQuery(
        'bills',
        'getAll',
        { billIds: [billId] },
        {
            select: ({ bills }) =>
                pipe(option.fromNullable(bills), option.flatMap(array.findFirst(({ id }) => id === billId))),
        }
    );

    const accountQuery = useRepositoryQuery(
        'accounts',
        'getAll',
        {
            accountIds: pipe(
                billQuery.isSuccess ? billQuery.data : option.none,
                option.match(
                    () => [],
                    ({ ownerId }) => [ownerId]
                )
            ),
        },
        {
            enabled: billQuery.isSuccess,
            select: (accountResult) =>
                pipe(
                    option.Do,
                    option.bind('bill', () => (billQuery.isSuccess ? billQuery.data : option.none)),
                    option.bind('owner', ({ bill }) => pipe(bill, getBillOwner(accountResult))),
                    option.map(({ bill, owner }) => {
                        const ownerAddressObjection =
                            allowCustomerBillAddressObjection && owner.type === AccountType.Customer
                                ? { ownerAddressObjection: false }
                                : {};

                        return {
                            billType: bill.type,
                            owner: {
                                address: getAddress(bill.ownerAddress),
                                name: getBillOwnerName(owner),
                                type: owner.type,
                                ...ownerAddressObjection,
                            },
                            associatedAccount: {
                                address: getAddress(bill.associatedAccountAddress),
                                associatedAccountAddressObjection: false,
                                id: bill.associatedAccountId ?? null,
                            },
                        };
                    })
                ),
        }
    );

    return {
        isFetching: billQuery.isFetching || accountQuery.isFetching,
        isError: billQuery.isError || accountQuery.isError,
        data: accountQuery.isSuccess ? accountQuery.data : option.none,
        billData: billQuery.isSuccess ? billQuery.data : option.none,
    };
};
